import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-zinc-950">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-4">Terms of Service</h1>
        <p className="text-zinc-400 mb-8">Last updated: January 8, 2025</p>

        <div className="prose prose-invert max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">1. Agreement to Terms</h2>
            <p className="text-zinc-300 mb-4">
              By accessing and using the Santorini Best Experience website and services, you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not use our services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">2. Services Provided</h2>
            <p className="text-zinc-300 mb-4">
              Santorini Best Experience provides transportation and tour services in Santorini, Greece, including:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 mb-4">
              <li>Airport and port transfers</li>
              <li>Point-to-point transportation</li>
              <li>Hourly tour services</li>
              <li>Customized island tours</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">3. Booking and Payment</h2>
            <h3 className="text-xl font-semibold text-white mb-3">3.1 Booking Process</h3>
            <p className="text-zinc-300 mb-4">
              All bookings must be made through our website. You must provide accurate and complete information during the booking process.
            </p>
            <h3 className="text-xl font-semibold text-white mb-3">3.2 Payment</h3>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 mb-4">
              <li>Payment must be completed at the time of booking via Stripe</li>
              <li>All prices are in Euros (€)</li>
              <li>Prices are based on distance for transfers (€36-50 base + per km) and hourly rates for tours</li>
              <li>Tour bookings require a minimum of 3 hours</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">4. Cancellation and Refund Policy</h2>
            <p className="text-zinc-300 mb-4">
              Please refer to our <Link to="/cancellation" className="text-amber-400 hover:text-amber-300">Cancellation Policy</Link> for detailed information about cancellations and refunds.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">5. Customer Responsibilities</h2>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 mb-4">
              <li>Arrive at the pickup location on time</li>
              <li>Provide accurate pickup and drop-off information</li>
              <li>Ensure the number of passengers does not exceed vehicle capacity</li>
              <li>Notify us of any special requirements in advance</li>
              <li>Treat our drivers and vehicles with respect</li>
              <li>Comply with Greek traffic laws and safety regulations</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">6. Liability and Insurance</h2>
            <h3 className="text-xl font-semibold text-white mb-3">6.1 Our Liability</h3>
            <p className="text-zinc-300 mb-4">
              All vehicles are fully insured according to Greek law. We maintain comprehensive insurance coverage for passenger transportation.
            </p>
            <h3 className="text-xl font-semibold text-white mb-3">6.2 Limitation of Liability</h3>
            <p className="text-zinc-300 mb-4">
              We are not liable for:
            </p>
            <ul className="list-disc list-inside text-zinc-300 space-y-2 mb-4">
              <li>Delays caused by traffic, weather, or circumstances beyond our control</li>
              <li>Personal belongings lost or damaged during the service</li>
              <li>Indirect or consequential damages</li>
              <li>Missed flights, ferries, or connections due to unforeseen circumstances</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">7. Force Majeure</h2>
            <p className="text-zinc-300 mb-4">
              We are not responsible for failure to perform services due to events beyond our reasonable control, including but not limited to: natural disasters, strikes, government actions, or other force majeure events.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">8. Intellectual Property</h2>
            <p className="text-zinc-300 mb-4">
              All content on this website, including text, graphics, logos, and images, is the property of Santorini Best Experience and protected by intellectual property laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">9. Privacy</h2>
            <p className="text-zinc-300 mb-4">
              Your use of our services is also governed by our <Link to="/privacy" className="text-amber-400 hover:text-amber-300">Privacy Policy</Link>.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">10. Governing Law</h2>
            <p className="text-zinc-300 mb-4">
              These Terms of Service are governed by the laws of Greece. Any disputes shall be subject to the exclusive jurisdiction of the courts of Santorini, Greece.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">11. Changes to Terms</h2>
            <p className="text-zinc-300 mb-4">
              We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to the website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">12. Contact Information</h2>
            <div className="text-zinc-300 space-y-2">
              <p><strong>Santorini Best Experience</strong></p>
              <p>Akrotiri, Santorini, Greece</p>
              <p>Phone: <a href="tel:+306974994577" className="text-amber-400 hover:text-amber-300">+30 697 499 4577</a></p>
              <p>Email: <a href="mailto:giorgos8956@gmail.com" className="text-amber-400 hover:text-amber-300">giorgos8956@gmail.com</a></p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
